/**
 * KS Build Your Own Box homepage builder.
 * Mirrors Simple Bundles BYOB cart payload via POST /cart/add.js (FormData).
 */
(function () {
  const STORAGE_PREFIX = "ks_byob_discovery_v2_";
  const FALLBACK_PACK_TITLE = "Pack option";

  function parseVariantCountMap(raw) {
    const map = {};
    if (!raw) return map;
    raw.split(/\r?\n/).forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || !trimmed.includes("=")) return;
      const [key, val] = trimmed.split("=").map((s) => s.trim());
      const num = parseInt(val, 10);
      if (key && num > 0) map[key] = num;
    });
    return map;
  }

  function parseRequiredCount(title, countMap) {
    if (countMap && countMap[title]) return countMap[title];
    const match = String(title).match(/(\d+)\s*$/);
    return match ? parseInt(match[1], 10) : 4;
  }

  function formatMoney(cents, sampleEl) {
    if (!sampleEl) return (cents / 100).toFixed(2);
    const sample = sampleEl.textContent || "";
    const decSep = sample.includes(",") ? "," : ".";
    const str = String(cents);
    const decLen = 2;
    const intPart = str.slice(0, -decLen) || "0";
    const decPart = str.slice(-decLen).padStart(decLen, "0");
    if (sample.includes(decSep)) {
      return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ") + decSep + decPart;
    }
    return intPart + decSep + decPart;
  }

  function getSessionMapKey(handle) {
    return STORAGE_PREFIX + handle;
  }

  function loadSessionDiscovery(handle) {
    try {
      const raw = sessionStorage.getItem(getSessionMapKey(handle));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object" || !parsed.hashMap) return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  function saveSessionDiscovery(handle, discovery) {
    try {
      sessionStorage.setItem(getSessionMapKey(handle), JSON.stringify(discovery));
    } catch (_) {
      /* ignore quota errors */
    }
  }

  function clearSessionDiscovery(handle) {
    try {
      sessionStorage.removeItem(getSessionMapKey(handle));
    } catch (_) {
      /* ignore */
    }
  }

  function absolutizeUrl(url) {
    if (!url) return "";
    if (url.startsWith("//")) return window.location.protocol + url;
    return url;
  }

  /**
   * Normalize Shopify CDN URLs so size variants of the same asset
   * (`_200x`, `_900x`, query width) collapse to one gallery entry.
   */
  function imageDedupeKey(url) {
    if (!url) return "";
    try {
      const parsed = new URL(absolutizeUrl(url), window.location.origin);
      let path = parsed.pathname || "";
      path = path
        .replace(/_(?:\d+x\d*|\d+x)(?:@\d+x)?(?=\.[a-zA-Z0-9]+$)/, "")
        .replace(/@\d+x(?=\.[a-zA-Z0-9]+$)/, "");
      return (parsed.hostname + path).toLowerCase();
    } catch (_) {
      return String(url)
        .replace(/[?&](width|height|crop|v)=[^&]*/g, "")
        .replace(/_(?:\d+x\d*|\d+x)(?:@\d+x)?(?=\.[a-zA-Z0-9]+)/, "")
        .toLowerCase();
    }
  }

  function dedupeImages(images) {
    const out = [];
    const seen = new Set();
    (images || []).forEach((img) => {
      if (!img) return;
      const src = typeof img === "string" ? img : img.src;
      if (!src) return;
      const key = imageDedupeKey(src);
      if (!key || seen.has(key)) return;
      seen.add(key);
      if (typeof img === "string") out.push({ src, thumb: src, alt: "" });
      else out.push({ src, thumb: img.thumb || src, alt: img.alt || "" });
    });
    return out;
  }

  /** Small CDN URL for pack-card hover thumbnails (keeps bandwidth low). */
  function toCardThumbUrl(url, width) {
    if (!url) return "";
    const w = width || 160;
    try {
      const parsed = new URL(absolutizeUrl(url), window.location.origin);
      if (!/cdn\.shopify\.com$/i.test(parsed.hostname) && !/\.myshopify\.com$/i.test(parsed.hostname)) {
        return url;
      }
      // Prefer query width when present; otherwise inject _NNNx before extension.
      if (parsed.searchParams.has("width")) {
        parsed.searchParams.set("width", String(w));
        return parsed.href;
      }
      parsed.pathname = parsed.pathname.replace(
        /_(?:\d+x\d*|\d+x)(?:@\d+x)?(?=\.[a-zA-Z0-9]+$)/,
        ""
      );
      parsed.pathname = parsed.pathname.replace(
        /(\.[a-zA-Z0-9]+)$/,
        "_" + w + "x$1"
      );
      return parsed.href;
    } catch (_) {
      return url;
    }
  }

  function toInt(value) {
    const num = parseInt(value, 10);
    return Number.isFinite(num) ? num : null;
  }

  /**
   * Reads the Simple Bundles BYOB widget out of the parent product page.
   *
   * Observed markup (Simple Bundles app embed):
   *   byob-bundle[currency-code][product-title][selected-variant-id]
   *     nav > byob-pack[variant-id][label][item-count]      -> box sizes
   *     byob-section[data-pack-variant-id]                  -> one per box size
   *       byob-product > byob-variant[variant-id][hash]     -> pack options
   *
   * byob-variant carries product-title, image-url, price-cents, available and
   * option-values, so Unlisted pack products can be rendered without a
   * collection. Each pack repeats once per box size with a stable hash, so
   * variant IDs are de-duplicated on first occurrence.
   */
  function parseDiscoveryFromHtml(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const hashMap = {};
    const packs = [];
    const seen = new Set();
    const boxSizes = {};

    const bundleEl = doc.querySelector("byob-bundle");
    const currencyCode = bundleEl ? bundleEl.getAttribute("currency-code") || "" : "";

    doc.querySelectorAll("byob-pack[variant-id][item-count]").forEach((el) => {
      const parentVariantId = el.getAttribute("variant-id");
      const itemCount = toInt(el.getAttribute("item-count"));
      const label = el.getAttribute("label") || "";
      if (!parentVariantId || !itemCount || itemCount <= 0) return;
      boxSizes[parentVariantId] = { itemCount, label };
    });

    doc.querySelectorAll("byob-variant[variant-id][hash]").forEach((el) => {
      const variantId = el.getAttribute("variant-id");
      const hash = el.getAttribute("hash");
      if (!variantId || !hash) return;

      hashMap[variantId] = hash;
      if (seen.has(variantId)) return;
      seen.add(variantId);

      const optionValues = (el.getAttribute("option-values") || "").trim();
      const title = (el.getAttribute("product-title") || "").trim();

      packs.push({
        variantId: variantId,
        hash: hash,
        title: title,
        optionValues: optionValues && optionValues !== "Default Title" ? optionValues : "",
        imageUrl: absolutizeUrl(el.getAttribute("image-url") || ""),
        imageWidth: toInt(el.getAttribute("image-width")),
        imageHeight: toInt(el.getAttribute("image-height")),
        priceCents: toInt(el.getAttribute("price-cents")),
        available: el.getAttribute("available") !== "false",
        productUrl: el.getAttribute("product-url") || "",
      });
    });

    return { hashMap, packs, boxSizes, currencyCode };
  }

  async function fetchDiscovery(productUrl) {
    const url =
      productUrl +
      (productUrl.includes("?") ? "&" : "?") +
      "ks_byob_discovery=1&ks_byob_hashmap=1";
    let lastError = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await fetch(url, {
          credentials: "same-origin",
          headers: { Accept: "text/html" },
        });
        if (!response.ok) throw new Error("BYOB discovery fetch failed");
        const html = await response.text();
        return parseDiscoveryFromHtml(html);
      } catch (error) {
        lastError = error;
        if (attempt === 0) {
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
      }
    }
    throw lastError || new Error("BYOB discovery fetch failed");
  }

  async function fetchHashMap(productUrl) {
    const discovery = await fetchDiscovery(productUrl);
    return discovery.hashMap;
  }

  class ByobBuilder {
    constructor(root) {
      this.root = root;
      this.productHandle = root.dataset.productHandle;
      this.productUrl = root.dataset.productUrl;
      this.countMap = parseVariantCountMap(root.dataset.variantCountMap || "");
      this.hashMap = {};
      this.packSourceMode = root.dataset.packSourceMode || "simple_bundles_auto";
      this.discoveredBoxSizes = {};
      this.discoveryCurrency = "";
      this.discoveryRendered = false;
      this.mode = "direct";
      this.selectionOrder = [];
      this.selections = new Map();
      this.drawerOpen = false;
      this.previousFocus = null;
      this.loading = false;
      this.drawerDoneText = root.dataset.drawerDoneText || "Done";
      this.checkoutForm = root.querySelector("[data-ks-byob-form]");
      this.summarySticky = root.querySelector("[data-ks-byob-summary-sticky]");
      this.toast = root.querySelector("[data-ks-byob-toast]");
      this.toastMessage = root.querySelector("[data-ks-byob-toast-message]");
      this.lastTotalSelected = 0;
      this._toastTimer = null;

      this.pills = root.querySelectorAll("[data-ks-byob-variant-pill]");
      this.activePill =
        root.querySelector(".ks-byob__variant-pill.is-active") || this.pills[0];

      // Temporary verification marker — remove after confirming shipping-final-step UX.
      console.log("KS BYOB shipping-final-step UX active");

      this.bindPackRows();
      this.bindPackDetailModal();
      this.bindVariantPills();
      this.bindSubmitButtons();
      this.bindDrawer();
      this.bindShippingListener();
      this.applyManualHashOverrides();
      this.syncDrawerBoxLabel();
      this.bootstrapHashMap();
      this.syncPackRowsFromState();
      this.updateUI();
    }

    bindPackRows() {
      if (this.root.dataset.ksByobPackBound === "true") return;
      this.root.dataset.ksByobPackBound = "true";

      this.root.querySelectorAll("[data-ks-byob-pack]").forEach((packEl) => {
        const variantId = packEl.dataset.variantId;
        if (!variantId) return;

        const min = parseInt(packEl.dataset.min || "0", 10);
        const max = parseInt(packEl.dataset.max || "99", 10);
        const defaultQty = parseInt(packEl.dataset.defaultQty || "0", 10);

        if (defaultQty > 0) {
          this.setPackQty(variantId, defaultQty, min, max);
        }
      });

      this.root.addEventListener("click", (event) => {
        const plus = event.target.closest("[data-ks-byob-pack-plus]");
        const minus = event.target.closest("[data-ks-byob-pack-minus]");
        const control = plus || minus;
        if (!control || !this.root.contains(control)) return;

        const packEl = control.closest("[data-ks-byob-pack]");
        if (!packEl) return;

        const variantId = packEl.dataset.variantId;
        if (!variantId) return;

        const min = parseInt(packEl.dataset.min || "0", 10);
        const max = parseInt(packEl.dataset.max || "99", 10);
        const current = this.selections.get(variantId) || 0;

        if (plus) {
          if (this.getTotalSelected() >= this.getRequiredCount()) return;
          this.setPackQty(variantId, current + 1, min, max);
          this.animateQtyPop(packEl);
        } else {
          this.setPackQty(variantId, current - 1, min, max);
        }

        this.syncPackRowsFromState();
        this.updateUI();
      });
    }

    bindPackDetailModal() {
      if (this.root.dataset.ksByobPackModalBound === "true") return;
      this.root.dataset.ksByobPackModalBound = "true";

      this.packModal = this.root.querySelector("[data-ks-byob-pack-modal]");
      if (!this.packModal) return;

      this.packModalHome = this.packModal.parentElement;
      this.packModalPanel = this.packModal.querySelector("[data-ks-byob-pack-modal-panel]");
      this.packModalTitle = this.packModal.querySelector("[data-ks-byob-pack-modal-title]");
      this.packModalSubtitle = this.packModal.querySelector("[data-ks-byob-pack-modal-subtitle]");
      this.packModalPrice = this.packModal.querySelector("[data-ks-byob-pack-modal-price]");
      this.packModalBody = this.packModal.querySelector("[data-ks-byob-pack-modal-body]");
      this.packModalLoading = this.packModal.querySelector("[data-ks-byob-pack-modal-loading]");
      this.packModalQtyWrap = this.packModal.querySelector("[data-ks-byob-pack-modal-qty]");
      this.packModalCount = this.packModal.querySelector("[data-ks-byob-pack-modal-count]");
      this.packModalMinus = this.packModal.querySelector("[data-ks-byob-pack-modal-minus]");
      this.packModalPlus = this.packModal.querySelector("[data-ks-byob-pack-modal-plus]");
      this.packGallery = this.packModal.querySelector("[data-ks-byob-pack-gallery]");
      this.packGalleryImage = this.packModal.querySelector("[data-ks-byob-pack-gallery-image]");
      this.packGalleryThumbs = this.packModal.querySelector("[data-ks-byob-pack-gallery-thumbs]");
      this.packGalleryPrev = this.packModal.querySelector("[data-ks-byob-pack-gallery-prev]");
      this.packGalleryNext = this.packModal.querySelector("[data-ks-byob-pack-gallery-next]");
      this.packGalleryMain = this.packModal.querySelector("[data-ks-byob-pack-gallery-main]");

      this.packModalOpen = false;
      this.packModalPreviousFocus = null;
      this.packModalActiveVariantId = null;
      this.packModalRequestId = 0;
      this.packDetailCache = Object.create(null);
      this.galleryImages = [];
      this.galleryIndex = 0;
      this.galleryAutoplayTimer = null;
      this.galleryAutoplayPaused = false;
      this.galleryUserPaused = false;
      this.galleryTouchStartX = null;

      this.packGalleryAutoplay =
        this.root.dataset.packGalleryAutoplay === "true" &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      this.packGalleryAutoplayDelay =
        Math.max(2, parseInt(this.root.dataset.packGalleryAutoplayDelay || "4", 10) || 4) * 1000;

      this.root.addEventListener("click", (event) => {
        const trigger = event.target.closest("[data-ks-byob-pack-detail-trigger]");
        if (!trigger || !this.root.contains(trigger)) return;
        if (event.target.closest("[data-ks-byob-pack-qty]")) return;

        const packEl = trigger.closest("[data-ks-byob-pack]");
        if (!packEl) return;

        event.preventDefault();
        this.openPackDetail(packEl, trigger);
      });

      this.packModal.addEventListener("click", (event) => {
        const closeBtn = event.target.closest(".ks-byob__pack-modal-close");
        if (closeBtn) {
          this.closePackDetail();
          return;
        }

        // Backdrop / outside the panel closes the drawer.
        const onBackdrop =
          event.target === this.packModal ||
          event.target.classList.contains("ks-byob__pack-modal-backdrop") ||
          event.target.matches("[data-ks-byob-pack-modal-close]:not(.ks-byob__pack-modal-close)");
        if (onBackdrop) {
          this.closePackDetail();
          return;
        }

        if (this.packModalPanel && !this.packModalPanel.contains(event.target)) {
          this.closePackDetail();
        }
      });

      // Capture-phase backup: some theme focus-trap helpers swallow bubble clicks.
      this.packModal.addEventListener(
        "pointerdown",
        (event) => {
          if (
            event.target === this.packModal ||
            event.target.classList.contains("ks-byob__pack-modal-backdrop")
          ) {
            this.closePackDetail();
          }
        },
        true
      );

      this.packModalMinus?.addEventListener("click", () => this.adjustModalPackQty(-1));
      this.packModalPlus?.addEventListener("click", () => this.adjustModalPackQty(1));

      this.packGalleryPrev?.addEventListener("click", () => {
        this.pauseGalleryAutoplayForUser();
        this.showGalleryImage(this.galleryIndex - 1);
      });
      this.packGalleryNext?.addEventListener("click", () => {
        this.pauseGalleryAutoplayForUser();
        this.showGalleryImage(this.galleryIndex + 1);
      });

      this.packGalleryThumbs?.addEventListener("click", (event) => {
        const thumb = event.target.closest("[data-ks-byob-pack-gallery-thumb]");
        if (!thumb) return;
        const index = parseInt(thumb.dataset.index || "0", 10);
        this.pauseGalleryAutoplayForUser();
        this.showGalleryImage(index);
      });

      if (this.packGalleryMain) {
        this.packGalleryMain.addEventListener("mouseenter", () => {
          this.galleryAutoplayPaused = true;
          this.stopGalleryAutoplay();
        });
        this.packGalleryMain.addEventListener("mouseleave", () => {
          this.galleryAutoplayPaused = false;
          if (!this.galleryUserPaused) this.startGalleryAutoplay();
        });
        this.packGalleryMain.addEventListener(
          "touchstart",
          (event) => {
            this.galleryTouchStartX = event.changedTouches?.[0]?.clientX ?? null;
          },
          { passive: true }
        );
        this.packGalleryMain.addEventListener(
          "touchend",
          (event) => {
            if (this.galleryTouchStartX == null) return;
            const endX = event.changedTouches?.[0]?.clientX;
            if (endX == null) return;
            const delta = endX - this.galleryTouchStartX;
            this.galleryTouchStartX = null;
            if (Math.abs(delta) < 40) return;
            this.pauseGalleryAutoplayForUser();
            this.showGalleryImage(this.galleryIndex + (delta < 0 ? 1 : -1));
          },
          { passive: true }
        );
      }

      this.packModalKeyHandler = (e) => {
        if (!this.packModalOpen) return;
        if (e.code === "Escape") {
          e.preventDefault();
          this.closePackDetail();
          return;
        }
        if (this.galleryImages.length < 2) return;
        if (e.code === "ArrowLeft") {
          e.preventDefault();
          this.pauseGalleryAutoplayForUser();
          this.showGalleryImage(this.galleryIndex - 1);
        } else if (e.code === "ArrowRight") {
          e.preventDefault();
          this.pauseGalleryAutoplayForUser();
          this.showGalleryImage(this.galleryIndex + 1);
        }
      };
      document.addEventListener("keydown", this.packModalKeyHandler);
    }

    parsePackImages(packEl) {
      const images = [];
      const push = (src, alt, thumb) => {
        if (!src) return;
        images.push({
          src,
          thumb: thumb || src,
          alt: alt || "",
        });
      };

      try {
        const raw = packEl.dataset.packImages;
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            parsed.forEach((item) => {
              if (!item) return;
              if (typeof item === "string") push(item, "", item);
              else push(item.src, item.alt, item.thumb);
            });
          }
        }
      } catch (_) {
        /* ignore malformed liquid JSON */
      }

      packEl
        .querySelectorAll("[data-ks-byob-pack-detail-image][data-image-url]")
        .forEach((el) => {
          push(el.dataset.imageUrl, el.dataset.imageAlt || "", el.dataset.imageUrl);
        });

      const cardImg = packEl.querySelector(".ks-byob__pack-image:not(.ks-byob__pack-image--secondary)");
      if (cardImg?.src) push(cardImg.src, cardImg.alt || "", cardImg.src);

      return dedupeImages(images);
    }

    productJsonUrlFromProductUrl(productUrl) {
      if (!productUrl) return "";
      try {
        const url = new URL(productUrl, window.location.origin);
        const match = url.pathname.match(/\/products\/([^/?#]+)/i);
        if (!match) return "";
        return "/products/" + match[1] + ".js";
      } catch (_) {
        return "";
      }
    }

    async fetchPackProductDetails(productUrl) {
      if (!productUrl) return null;
      if (this.packDetailCache[productUrl]) return this.packDetailCache[productUrl];

      const jsonUrl = this.productJsonUrlFromProductUrl(productUrl);
      if (jsonUrl) {
        try {
          const response = await fetch(jsonUrl, {
            credentials: "same-origin",
            headers: { Accept: "application/json" },
          });
          if (response.ok) {
            const data = await response.json();
            const images = [];
            const alts = data.media || [];
            (data.images || []).forEach((src, index) => {
              const media = alts[index];
              images.push({
                src: typeof src === "string" ? src : src?.src || "",
                thumb: typeof src === "string" ? src : src?.src || "",
                alt: media?.alt || data.title || "",
              });
            });
            if (!images.length && data.featured_image) {
              images.push({
                src: data.featured_image,
                thumb: data.featured_image,
                alt: data.title || "",
              });
            }
            const result = {
              title: data.title || "",
              description: data.description || "",
              images: dedupeImages(images.filter((img) => img.src)),
              priceCents: data.price != null ? Number(data.price) : null,
            };
            this.packDetailCache[productUrl] = result;
            return result;
          }
        } catch (_) {
          /* fall through to HTML parse */
        }
      }

      try {
        const response = await fetch(productUrl, {
          credentials: "same-origin",
          headers: { Accept: "text/html" },
        });
        if (!response.ok) return null;
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const descriptionEl =
          doc.querySelector(".product__description") ||
          doc.querySelector("[data-product-description]") ||
          doc.querySelector(".product-single__description") ||
          doc.querySelector(".rte");
        const images = [];
        // Prefer product media gallery nodes only — avoid scraping every <img>
        // on the page (srcset clones, thumbs, related products).
        const mediaRoot =
          doc.querySelector("media-gallery") ||
          doc.querySelector("product-gallery") ||
          doc.querySelector(".product__media-list") ||
          doc.querySelector(".product__media") ||
          doc.querySelector(".product-media");
        const mediaImgs = mediaRoot
          ? mediaRoot.querySelectorAll("img")
          : doc.querySelectorAll(".product__media img, .product-media img");
        mediaImgs.forEach((img) => {
          const src =
            img.getAttribute("data-src") ||
            img.getAttribute("data-zoom") ||
            img.currentSrc ||
            img.src;
          if (src) images.push({ src, thumb: src, alt: img.alt || "" });
        });
        if (!images.length) {
          const og = doc.querySelector('meta[property="og:image"]');
          if (og?.content) images.push({ src: og.content, thumb: og.content, alt: "" });
        }
        const result = {
          title: doc.querySelector("h1")?.textContent?.trim() || "",
          description: descriptionEl ? descriptionEl.innerHTML.trim() : "",
          images: dedupeImages(images),
          priceCents: null,
        };
        this.packDetailCache[productUrl] = result;
        return result;
      } catch (_) {
        return null;
      }
    }

    setGalleryImages(images) {
      this.galleryImages = dedupeImages(images);
      this.galleryIndex = 0;
      this.galleryUserPaused = false;
      this.stopGalleryAutoplay();

      if (!this.packGallery || !this.packGalleryImage) return;

      if (!this.galleryImages.length) {
        this.packGallery.hidden = true;
        this.packGalleryImage.removeAttribute("src");
        if (this.packGalleryThumbs) {
          this.packGalleryThumbs.innerHTML = "";
          this.packGalleryThumbs.hidden = true;
        }
        if (this.packGalleryPrev) this.packGalleryPrev.hidden = true;
        if (this.packGalleryNext) this.packGalleryNext.hidden = true;
        return;
      }

      this.packGallery.hidden = false;
      const multi = this.galleryImages.length > 1;
      if (this.packGalleryPrev) this.packGalleryPrev.hidden = !multi;
      if (this.packGalleryNext) this.packGalleryNext.hidden = !multi;

      if (this.packGalleryThumbs) {
        this.packGalleryThumbs.innerHTML = "";
        if (multi) {
          this.packGalleryThumbs.hidden = false;
          this.galleryImages.forEach((img, index) => {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "ks-byob__pack-gallery-thumb";
            btn.setAttribute("data-ks-byob-pack-gallery-thumb", "");
            btn.dataset.index = String(index);
            btn.setAttribute("role", "option");
            btn.setAttribute("aria-label", "View image " + (index + 1));
            const thumbImg = document.createElement("img");
            thumbImg.src = img.thumb || img.src;
            thumbImg.alt = "";
            thumbImg.loading = "lazy";
            btn.appendChild(thumbImg);
            this.packGalleryThumbs.appendChild(btn);
          });
        } else {
          this.packGalleryThumbs.hidden = true;
        }
      }

      this.showGalleryImage(0);
      this.startGalleryAutoplay();
    }

    showGalleryImage(index) {
      if (!this.galleryImages.length || !this.packGalleryImage) return;
      const len = this.galleryImages.length;
      this.galleryIndex = ((index % len) + len) % len;
      const image = this.galleryImages[this.galleryIndex];
      this.packGalleryImage.src = image.src;
      this.packGalleryImage.alt = image.alt || "";

      let activeThumb = null;
      this.packGalleryThumbs?.querySelectorAll("[data-ks-byob-pack-gallery-thumb]").forEach((thumb) => {
        const isActive = parseInt(thumb.dataset.index || "-1", 10) === this.galleryIndex;
        thumb.classList.toggle("is-active", isActive);
        thumb.setAttribute("aria-selected", isActive ? "true" : "false");
        if (isActive) activeThumb = thumb;
      });

      // Keep the active thumb in the visible portion of the strip only
      // (avoid scrollIntoView, which can also jump the drawer panel).
      if (activeThumb && this.packGalleryThumbs) {
        const scroller = this.packGalleryThumbs;
        const target =
          activeThumb.offsetLeft - (scroller.clientWidth - activeThumb.offsetWidth) / 2;
        const left = Math.max(0, Math.min(target, scroller.scrollWidth - scroller.clientWidth));
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (typeof scroller.scrollTo === "function") {
          scroller.scrollTo({ left, behavior: reduceMotion ? "auto" : "smooth" });
        } else {
          scroller.scrollLeft = left;
        }
      }
    }

    startGalleryAutoplay() {
      this.stopGalleryAutoplay();
      if (!this.packGalleryAutoplay || this.galleryUserPaused || this.galleryAutoplayPaused) return;
      if (!this.packModalOpen || this.galleryImages.length < 2) return;

      this.galleryAutoplayTimer = window.setInterval(() => {
        this.showGalleryImage(this.galleryIndex + 1);
      }, this.packGalleryAutoplayDelay);
    }

    stopGalleryAutoplay() {
      if (this.galleryAutoplayTimer) {
        window.clearInterval(this.galleryAutoplayTimer);
        this.galleryAutoplayTimer = null;
      }
    }

    pauseGalleryAutoplayForUser() {
      this.galleryUserPaused = true;
      this.stopGalleryAutoplay();
    }

    syncModalQtyControls() {
      if (!this.packModalActiveVariantId || !this.packModalQtyWrap) return;
      const packEl = this.root.querySelector(
        `[data-ks-byob-pack][data-variant-id="${this.packModalActiveVariantId}"]`
      );
      if (!packEl) {
        this.packModalQtyWrap.hidden = true;
        return;
      }

      const qty = this.selections.get(this.packModalActiveVariantId) || 0;
      const min = parseInt(packEl.dataset.min || "0", 10);
      const max = parseInt(packEl.dataset.max || "99", 10);
      const totalWithout = this.getTotalSelected() - qty;
      const room = Math.max(0, this.getRequiredCount() - totalWithout);
      const effectiveMax = Math.min(max, room);

      if (this.packModalCount) this.packModalCount.textContent = String(qty);
      if (this.packModalMinus) this.packModalMinus.disabled = qty <= 0 || qty <= min;
      if (this.packModalPlus) this.packModalPlus.disabled = qty >= effectiveMax;
      this.packModalQtyWrap.hidden = false;
    }

    adjustModalPackQty(delta) {
      if (!this.packModalActiveVariantId) return;
      const packEl = this.root.querySelector(
        `[data-ks-byob-pack][data-variant-id="${this.packModalActiveVariantId}"]`
      );
      if (!packEl) return;
      const min = parseInt(packEl.dataset.min || "0", 10);
      const max = parseInt(packEl.dataset.max || "99", 10);
      const current = this.selections.get(this.packModalActiveVariantId) || 0;
      if (delta > 0 && this.getTotalSelected() >= this.getRequiredCount()) return;
      this.setPackQty(this.packModalActiveVariantId, current + delta, min, max);
      this.syncPackRowsFromState();
      this.updateUI();
      this.syncModalQtyControls();
      if (delta > 0) this.animateQtyPop(packEl);
    }

    async openPackDetail(packEl, trigger) {
      if (!this.packModal || !packEl) return;

      const source = packEl.querySelector(".ks-byob__pack-detail-source");
      if (!source) return;

      const wasOpen = this.packModalOpen;
      const requestId = ++this.packModalRequestId;
      this.packModalActiveVariantId = packEl.dataset.variantId || null;

      const titleEl = source.querySelector(".ks-byob__pack-detail-title-source");
      const subtitleEl = source.querySelector(".ks-byob__pack-detail-subtitle-source");
      const descriptionEl = source.querySelector(".ks-byob__pack-detail-description-source");
      const title =
        titleEl?.textContent?.trim() ||
        packEl.querySelector(".ks-byob__pack-title")?.textContent?.trim() ||
        "";
      const subtitle =
        subtitleEl?.textContent?.trim() ||
        packEl.querySelector(".ks-byob__pack-subtitle")?.textContent?.trim() ||
        "";
      let description = descriptionEl ? descriptionEl.innerHTML.trim() : "";
      let images = this.parsePackImages(packEl);
      const productUrl = packEl.dataset.productUrl || "";
      const priceCents = parseInt(packEl.dataset.priceCents || "", 10);
      const needsFetch = !!productUrl && (!description || images.length < 2);

      if (this.packModalTitle) this.packModalTitle.textContent = title;

      if (this.packModalSubtitle) {
        if (subtitle) {
          this.packModalSubtitle.textContent = subtitle;
          this.packModalSubtitle.hidden = false;
        } else {
          this.packModalSubtitle.textContent = "";
          this.packModalSubtitle.hidden = true;
        }
      }

      if (this.packModalPrice) {
        if (Number.isFinite(priceCents)) {
          this.packModalPrice.textContent = this.formatDisplayMoney(
            priceCents,
            this.root.querySelector("[data-ks-byob-price]")
          );
          this.packModalPrice.hidden = false;
        } else {
          const cardPrice = packEl.querySelector(".ks-byob__pack-price")?.textContent?.trim();
          if (cardPrice) {
            this.packModalPrice.textContent = cardPrice;
            this.packModalPrice.hidden = false;
          } else {
            this.packModalPrice.textContent = "";
            this.packModalPrice.hidden = true;
          }
        }
      }

      if (this.packModalBody) {
        if (description) {
          this.packModalBody.innerHTML = description;
          this.packModalBody.hidden = false;
        } else {
          this.packModalBody.innerHTML = "";
          this.packModalBody.hidden = true;
        }
      }

      this.setGalleryImages(images);
      this.syncModalQtyControls();

      if (this.packModalLoading) {
        this.packModalLoading.hidden = !needsFetch;
      }

      if (!wasOpen) {
        this.packModalPreviousFocus = trigger || document.activeElement;
        if (this.packModal.parentElement !== document.body) {
          document.body.appendChild(this.packModal);
        }
        this.packModal.hidden = false;
        document.body.classList.add("ks-byob-pack-modal-open");
        this.packModalOpen = true;
        requestAnimationFrame(() => {
          this.packModal.classList.add("is-open");
        });

        const closeBtn = this.packModal.querySelector(
          "[data-ks-byob-pack-modal-close].ks-byob__pack-modal-close"
        );
        setTimeout(async () => {
          try {
            const helper = await import(
              window._resolveImport?.("helper") ||
                window._importmap?.imports?.helper ||
                "helper"
            );
            // Drawer focus trap would otherwise keep focus/interaction inside
            // the mobile BYOB sheet underneath this pack detail modal.
            if (this.drawerOpen) {
              helper.removeTrapFocus?.(this.packModalPreviousFocus);
            }
            helper.trapFocus?.(this.packModalPanel || this.packModal, closeBtn);
          } catch (_) {
            closeBtn?.focus();
          }
        }, 50);
      }

      if (!needsFetch) return;

      const fetched = await this.fetchPackProductDetails(productUrl);
      if (requestId !== this.packModalRequestId || !fetched) {
        if (requestId === this.packModalRequestId && this.packModalLoading) {
          this.packModalLoading.hidden = true;
        }
        return;
      }

      if (!description && fetched.description) {
        description = fetched.description;
        if (this.packModalBody) {
          this.packModalBody.innerHTML = description;
          this.packModalBody.hidden = false;
        }
        // Cache onto the pack row so reopening is instant.
        let descSource = source.querySelector(".ks-byob__pack-detail-description-source");
        if (!descSource) {
          descSource = document.createElement("div");
          descSource.className = "ks-byob__pack-detail-description-source";
          source.appendChild(descSource);
        }
        descSource.innerHTML = description;
      }

      if (fetched.images?.length) {
        // Prefer fetched product media when available; keep any pack-only
        // images that aren't already represented by CDN size variants.
        images = dedupeImages([...fetched.images, ...images]);
        this.setGalleryImages(images);
        this.applyPackImagesToRows(
          packEl.dataset.variantId,
          images,
          title
        );
      }

      if (this.packModalLoading) this.packModalLoading.hidden = true;
    }

    ensurePackCardSecondaryImage(packEl, images, title) {
      if (!packEl || !images || images.length < 2) return;
      const media = packEl.querySelector(".ks-byob__pack-media");
      if (!media) return;
      media.classList.add("ks-byob__pack-media--has-secondary");
      let secondary = media.querySelector(".ks-byob__pack-image--secondary");
      if (!secondary) {
        secondary = document.createElement("img");
        secondary.className = "ks-byob__pack-image ks-byob__pack-image--secondary";
        secondary.alt = title || "";
        secondary.decoding = "async";
        media.appendChild(secondary);
      }
      // Card hover only needs a tiny thumb; drawer still uses full-size gallery.
      const thumbSrc = toCardThumbUrl(images[1].thumb || images[1].src, 160);
      if (secondary.src !== thumbSrc) {
        secondary.loading = "eager";
        secondary.src = thumbSrc;
      }
    }

    applyPackImagesToRows(variantId, images, title) {
      if (!variantId || !images?.length) return;
      const unique = dedupeImages(images);
      this.root
        .querySelectorAll(`[data-ks-byob-pack][data-variant-id="${variantId}"]`)
        .forEach((packEl) => {
          packEl.dataset.packImages = JSON.stringify(unique);
          this.ensurePackCardSecondaryImage(packEl, unique, title);
        });
    }

    /**
     * After packs paint, quietly fetch product.js for cards missing a second
     * image so desktop hover works without opening the detail drawer first.
     * Desktop/hover only, idle-scheduled, concurrency-capped.
     */
    scheduleHoverImagePrefetch() {
      if (this._hoverPrefetchScheduled) return;
      if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

      this._hoverPrefetchScheduled = true;
      const start = () => {
        this._hoverPrefetchScheduled = false;
        this.prefetchHoverImages();
      };

      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(start, { timeout: 2500 });
      } else {
        window.setTimeout(start, 900);
      }
    }

    async prefetchHoverImages() {
      if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
      if (document.hidden) return;

      const candidates = [];
      const seen = new Set();
      this.root.querySelectorAll("[data-ks-byob-pack][data-product-url]").forEach((packEl) => {
        const variantId = packEl.dataset.variantId;
        const productUrl = packEl.dataset.productUrl;
        if (!variantId || !productUrl || seen.has(variantId)) return;
        if (packEl.querySelector(".ks-byob__pack-media--has-secondary")) return;

        // Skip if packImages already has 2+ unique images.
        try {
          const existing = packEl.dataset.packImages
            ? dedupeImages(JSON.parse(packEl.dataset.packImages))
            : [];
          if (existing.length >= 2) {
            this.ensurePackCardSecondaryImage(
              packEl,
              existing,
              packEl.querySelector(".ks-byob__pack-title")?.textContent?.trim() || ""
            );
            return;
          }
        } catch (_) {
          /* continue to fetch */
        }

        seen.add(variantId);
        candidates.push(packEl);
      });

      if (!candidates.length) return;

      const concurrency = 2;
      let index = 0;

      const worker = async () => {
        while (index < candidates.length) {
          if (document.hidden) return;
          const packEl = candidates[index++];
          const productUrl = packEl.dataset.productUrl;
          const variantId = packEl.dataset.variantId;
          const title =
            packEl.querySelector(".ks-byob__pack-title")?.textContent?.trim() || "";

          try {
            const fetched = await this.fetchPackProductDetails(productUrl);
            if (!fetched?.images?.length) continue;

            let localImages = [];
            try {
              localImages = packEl.dataset.packImages
                ? JSON.parse(packEl.dataset.packImages)
                : [];
            } catch (_) {
              localImages = [];
            }
            const merged = dedupeImages([...(fetched.images || []), ...localImages]);
            if (merged.length < 2) continue;

            this.applyPackImagesToRows(variantId, merged, title);

            // Warm browser cache for the tiny hover thumb.
            const warm = new Image();
            warm.decoding = "async";
            warm.src = toCardThumbUrl(merged[1].thumb || merged[1].src, 160);
          } catch (_) {
            /* non-blocking prefetch */
          }
        }
      };

      const runners = [];
      for (let i = 0; i < Math.min(concurrency, candidates.length); i++) {
        runners.push(worker());
      }
      await Promise.all(runners);
    }

    closePackDetail() {
      if (!this.packModal || !this.packModalOpen) return;

      this.stopGalleryAutoplay();
      this.packModal.classList.remove("is-open");
      this.packModalOpen = false;
      this.packModalActiveVariantId = null;
      this.packModalRequestId += 1;
      document.body.classList.remove("ks-byob-pack-modal-open");

      setTimeout(() => {
        this.packModal.hidden = true;
        if (this.packModalHome && this.packModal.parentElement !== this.packModalHome) {
          this.packModalHome.appendChild(this.packModal);
        }
      }, 280);

      const returnFocus = this.packModalPreviousFocus;
      import(window._resolveImport?.("helper") || "helper")
        .then((helper) => {
          helper.removeTrapFocus?.(returnFocus);
          if (this.drawerOpen) {
            const drawer = this.root.querySelector("[data-ks-byob-drawer]");
            const closeBtn = drawer?.querySelector(
              "[data-ks-byob-close-drawer].ks-byob__drawer-close"
            );
            const panel = drawer?.querySelector(".ks-byob__drawer-panel");
            if (panel) helper.trapFocus?.(panel, closeBtn || returnFocus);
          }
        })
        .catch(() => {});

      returnFocus?.focus?.();
    }

    animateQtyPop(packEl) {
      if (!packEl || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
      }

      packEl.querySelectorAll("[data-ks-byob-pack-count]").forEach((countEl) => {
        countEl.classList.remove("is-popping");
        void countEl.offsetWidth;
        countEl.classList.add("is-popping");
        const onEnd = () => {
          countEl.classList.remove("is-popping");
          countEl.removeEventListener("animationend", onEnd);
        };
        countEl.addEventListener("animationend", onEnd);
      });
    }

    celebrateBoxFull() {
      if (!this.summarySticky || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
      }

      this.summarySticky.classList.remove("is-box-full-celebrate");
      void this.summarySticky.offsetWidth;
      this.summarySticky.classList.add("is-box-full-celebrate");

      const onEnd = () => {
        this.summarySticky.classList.remove("is-box-full-celebrate");
        this.summarySticky.removeEventListener("animationend", onEnd);
      };
      this.summarySticky.addEventListener("animationend", onEnd);
    }

    showToast(message) {
      if (!this.toast) return;

      if (this.toastMessage && message) {
        this.toastMessage.textContent = message;
      }

      clearTimeout(this._toastTimer);
      this.toast.hidden = false;
      requestAnimationFrame(() => {
        this.toast.classList.add("is-visible");
      });

      this._toastTimer = setTimeout(() => {
        this.toast.classList.remove("is-visible");
        setTimeout(() => {
          this.toast.hidden = true;
        }, 320);
      }, 2600);
    }

    hasPackRows() {
      return this.root.querySelectorAll("[data-ks-byob-pack]").length > 0;
    }

    bindVariantPills() {
      this.pills = this.root.querySelectorAll("[data-ks-byob-variant-pill]");

      this.pills.forEach((pill) => {
        pill.addEventListener("click", () => {
          if (pill.disabled) return;

          const variantId = pill.dataset.variantId;
          this.pills.forEach((p) => {
            p.classList.toggle("is-active", p.dataset.variantId === variantId);
          });
          this.activePill =
            this.root.querySelector(
              `.ks-byob__variant-pill.is-active[data-variant-id="${variantId}"]`
            ) || pill;

          this.syncDrawerBoxLabel();
          this.lastTotalSelected = this.getTotalSelected();
          this.syncPackRowsFromState();
          this.updateUI();
        });
      });
    }

    syncDrawerBoxLabel() {
      const labelEl = this.root.querySelector("[data-ks-byob-drawer-box-label]");
      if (!labelEl || !this.activePill) return;
      labelEl.textContent = this.activePill.dataset.variantTitle || "";
    }

    bindSubmitButtons() {
      this.root.querySelectorAll("[data-ks-byob-submit]").forEach((btn) => {
        btn.addEventListener("click", (e) => this.handleAddToCart(e));
      });
    }

    bindDrawer() {
      const drawer = this.root.querySelector("[data-ks-byob-drawer]");
      if (!drawer) return;

      this.drawerDoneBtn = drawer.querySelector("[data-ks-byob-drawer-done]");

      this.root.querySelectorAll("[data-ks-byob-open-drawer]").forEach((btn) => {
        btn.addEventListener("click", () => this.openDrawer(btn));
      });

      this.root.querySelectorAll("[data-ks-byob-close-drawer]").forEach((btn) => {
        btn.addEventListener("click", () => this.handleDrawerClose());
      });

      this.drawerDoneBtn?.addEventListener("click", () => this.handleDrawerDone());

      this.drawerKeyHandler = (e) => {
        if (e.code === "Escape" && this.drawerOpen) {
          e.preventDefault();
          this.handleDrawerClose();
        }
      };
      document.addEventListener("keydown", this.drawerKeyHandler);
    }

    bindShippingListener() {
      if (!this.checkoutForm || this.root.dataset.ksByobShippingBound === "true") return;
      this.root.dataset.ksByobShippingBound = "true";

      const select = this.checkoutForm.querySelector("[data-ks-shipping-select]");
      if (!select) return;

      select.addEventListener("change", () => {
        this.updateUI();
        const total = this.getTotalSelected();
        const required = this.getRequiredCount();
        if (this.isShippingSelected() && total === required) {
          setTimeout(() => this.scrollToAddToCart(), 200);
        }
      });

      if (window.KSShippingOption?.init) {
        window.KSShippingOption.init(this.checkoutForm);
      }
    }

    isShippingSelected() {
      if (window.KSBoxSizeAtcWorkflow) return true;
      if (!window.KSShippingOption || !this.checkoutForm) return true;
      return Boolean(
        window.KSShippingOption.getSelectedOption(this.checkoutForm)
      );
    }

    getDrawerDoneLabel(total, required) {
      if (total === 0) return `Choose ${required} packs`;
      if (total < required) {
        const remaining = required - total;
        return `Choose ${remaining} more pack${remaining === 1 ? "" : "s"}`;
      }
      if (total > required) {
        const excess = total - required;
        return `Remove ${excess} pack${excess === 1 ? "" : "s"}`;
      }
      return this.drawerDoneText;
    }

    handleDrawerDone() {
      const total = this.getTotalSelected();
      const required = this.getRequiredCount();
      if (total !== required) return;

      // Prefer completing inside the drawer when shipping/ATC are already shown.
      const drawerSlot = this.root.querySelector("[data-ks-byob-drawer-checkout]");
      if (drawerSlot && !drawerSlot.hidden) {
        const select = this.checkoutForm?.querySelector("[data-ks-shipping-select]");
        try {
          select?.focus({ preventScroll: true });
        } catch (_) {
          select?.focus();
        }
        return;
      }

      this.closeDrawer({ focusShipping: true });
    }

    handleDrawerClose() {
      const total = this.getTotalSelected();
      const required = this.getRequiredCount();
      this.closeDrawer({
        focusShipping: total === required,
      });
    }

    highlightShippingNextStep(shouldHighlight) {
      const wrapper = this.root.querySelector(
        "[data-ks-byob-checkout] [data-ks-shipping-selector]"
      );
      const callout = this.root.querySelector("[data-ks-byob-next-step-callout]");
      if (!wrapper) return;

      wrapper.classList.toggle("ks-shipping-option--next-step", shouldHighlight);
      if (callout) callout.hidden = !shouldHighlight;
    }

    highlightAddToCartNextStep(shouldHighlight) {
      const submit = this.root.querySelector("[data-ks-byob-submit]");
      const callout = this.root.querySelector("[data-ks-byob-add-callout]");
      if (submit) {
        submit.classList.toggle("ks-byob__submit--next-step", shouldHighlight);
      }
      if (callout) callout.hidden = !shouldHighlight;
    }

    isMobileDrawerMode() {
      return (
        this.root.dataset.enableDrawer === "true" &&
        window.matchMedia("(max-width: 989px)").matches
      );
    }

    updateCheckoutDockVisibility(exact) {
      const dock = this.root.querySelector("[data-ks-byob-checkout]");
      const home = this.root.querySelector("[data-ks-byob-checkout-home]");
      const drawerSlot = this.root.querySelector("[data-ks-byob-drawer-checkout]");
      const doneBtn = this.drawerDoneBtn;
      if (!dock) return;

      // Shipping is a final step: collapse it completely until the box is exact.
      this.root.querySelectorAll("[data-ks-byob-shipping-wrap], [data-ks-shipping-selector]").forEach((wrap) => {
        wrap.setAttribute("hidden", "");
        wrap.setAttribute("aria-hidden", "true");
        wrap.inert = true;
      });

      if (!exact) {
        this.root
          .querySelectorAll("[data-ks-byob-next-step-callout], [data-ks-byob-add-callout]")
          .forEach((el) => {
            el.hidden = true;
          });
      }

      const mobileDrawer = this.isMobileDrawerMode();
      const showInDrawer = mobileDrawer && this.drawerOpen && exact;

      if (showInDrawer && drawerSlot) {
        if (dock.parentElement !== drawerSlot) {
          drawerSlot.appendChild(dock);
        }
        drawerSlot.hidden = false;
        dock.removeAttribute("hidden");
        if (doneBtn) doneBtn.hidden = true;
      } else {
        if (home && dock.parentElement !== home) {
          home.appendChild(dock);
        }
        if (drawerSlot) drawerSlot.hidden = true;

        if (mobileDrawer) {
          // Mobile drawer flow: keep the page dock collapsed until exact + drawer closed.
          if (exact && !this.drawerOpen) dock.removeAttribute("hidden");
          else dock.setAttribute("hidden", "");
        } else {
          // Desktop: keep ATC visible; shipping wrap above collapses until exact.
          dock.removeAttribute("hidden");
        }

        if (doneBtn) doneBtn.hidden = false;
      }

      if (exact) {
        this.preloadCartSidebar();
      }
    }

    scrollToAddToCart() {
      const submit = this.root.querySelector("[data-ks-byob-submit]");
      if (!submit) return;

      requestAnimationFrame(() => {
        try {
          submit.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch (_) {
          submit.scrollIntoView();
        }
        setTimeout(() => {
          try {
            submit.focus({ preventScroll: true });
          } catch (_) {
            submit.focus();
          }
        }, 350);
      });
    }

    scrollToMobileCheckout(options = {}) {
      const anchor = this.root.querySelector("[data-ks-byob-mobile-anchor]");
      const checkout = this.root.querySelector("[data-ks-byob-checkout]");
      const target =
        options.focusShipping && checkout ? checkout : anchor || checkout;
      if (!target) return;

      requestAnimationFrame(() => {
        try {
          target.scrollIntoView({
            behavior: options.instant ? "auto" : "smooth",
            block: options.focusShipping ? "end" : "center",
          });
        } catch (_) {
          target.scrollIntoView();
        }

        if (options.focusShipping) {
          setTimeout(() => {
            const select = this.checkoutForm?.querySelector("[data-ks-shipping-select]");
            try {
              select?.focus({ preventScroll: true });
            } catch (_) {
              select?.focus();
            }
          }, options.instant ? 50 : 450);
        }
      });
    }

    clearBodyScrollLock() {
      document.body.classList.remove("ks-byob-drawer-open");
      if (!document.body.dataset.cartStatus || document.body.dataset.cartStatus !== "open") {
        document.body.style.overflow = "";
        document.body.style.width = "";
      }
    }

    getCartSidebar() {
      const bodyCart = document.body.querySelector(":scope > cart-sidebar#cart-sidebar");
      if (bodyCart) return bodyCart;
      return document.getElementById("cart-sidebar") || document.querySelector("cart-sidebar");
    }

    hideDrawerImmediately() {
      const drawer = this.root.querySelector("[data-ks-byob-drawer]");
      if (!drawer) return;
      drawer.classList.remove("is-open");
      drawer.hidden = true;
      this.drawerOpen = false;
      this.clearBodyScrollLock();

      // Restore checkout dock to its home slot before the next paint.
      const dock = this.root.querySelector("[data-ks-byob-checkout]");
      const home = this.root.querySelector("[data-ks-byob-checkout-home]");
      const drawerSlot = this.root.querySelector("[data-ks-byob-drawer-checkout]");
      if (dock && home && dock.parentElement !== home) {
        home.appendChild(dock);
      }
      if (drawerSlot) drawerSlot.hidden = true;
      if (this.drawerDoneBtn) this.drawerDoneBtn.hidden = false;
    }

    async waitForCartMethods(timeoutMs = 8000) {
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        const cart = this.getCartSidebar();
        if (
          cart &&
          typeof cart.getSectionsToRender === "function" &&
          typeof cart.open === "function"
        ) {
          return cart;
        }
        await new Promise((resolve) => setTimeout(resolve, 40));
      }
      return this.getCartSidebar();
    }

    preloadCartSidebar() {
      if (!this._cartPreloadPromise) {
        this._cartPreloadPromise = this.ensureCartReady();
      }
      return this._cartPreloadPromise;
    }

    async ensureCartReady() {
      let cart = this.getCartSidebar();
      if (!cart) return null;

      try {
        const deferred = await import(
          window._resolveImport?.("deferred") ||
            window._importmap?.imports?.["deferred"] ||
            "deferred"
        );
        const { load: loadComponent, deferredItems } = deferred;
        const cartDeferred = deferredItems?.cartSidebar;

        if (cartDeferred && !cartDeferred.loaded) {
          loadComponent("cartSidebar");
        } else if (!customElements.get("cart-sidebar")) {
          loadComponent("cartSidebar");
        }

        if (cartDeferred?.promise) {
          await cartDeferred.promise;
        }

        if (typeof customElements !== "undefined") {
          await customElements.whenDefined("cart-sidebar");
        }

        cart = await this.waitForCartMethods();
      } catch (_) {
        /* ignore */
      }

      return cart;
    }

    applyRenderedCartSections(cart, data) {
      if (!data?.sections || typeof cart.getSectionsToRender !== "function") {
        return false;
      }

      let updated = false;
      cart.getSectionsToRender().forEach((section) => {
        const html = data.sections[section.id];
        if (!html) return;
        const target = document.getElementById(section.id);
        if (!target) return;
        const inner = cart.getSectionInnerHTML(html, section.selector);
        if (!inner) return;
        target.innerHTML = inner;
        updated = true;
      });

      if (updated) cart.classList.remove("updating");
      return updated;
    }

    forceOpenCart(cart, trigger) {
      if (!cart) return;

      this.hideDrawerImmediately();
      this.clearBodyScrollLock();
      cart.hidden = false;

      if (typeof cart.setActiveElement === "function" && trigger) {
        cart.setActiveElement(trigger);
      }

      const tryOpen = () => {
        if (document.body.classList.contains("cart")) return;

        cart.hidden = false;

        if (typeof cart.revealStickyHeaderAndOpen === "function") {
          if (cart.revealStickyHeaderAndOpen(trigger)) return;
        }

        if (cart.dataset.status !== "open" && typeof cart.open === "function") {
          cart.open(trigger);
          return;
        }

        if (cart.dataset.status !== "open") {
          cart.dataset.status = "open";
          document.body.dataset.cartStatus = "open";
          if (cart.parentNode?.tagName === "BODY") {
            document.body.style.overflow = "hidden";
            document.body.style.width = `${window.visualViewport?.width || window.innerWidth}px`;
          }
        }
      };

      tryOpen();
      setTimeout(tryOpen, 120);
      setTimeout(tryOpen, 450);
    }

    async openCartAfterAdd(cart, data, trigger) {
      this.hideDrawerImmediately();
      this.clearBodyScrollLock();
      cart.hidden = false;

      if (typeof cart.renderContents === "function" && data?.sections) {
        try {
          cart.renderContents(data);
          return;
        } catch (err) {
          console.warn("[KS BYOB] renderContents failed, falling back", err);
        }
      }

      const sectionsApplied = this.applyRenderedCartSections(cart, data);

      if (!sectionsApplied && typeof cart.reload === "function") {
        try {
          await cart.reload();
        } catch (_) {
          /* ignore */
        }
      }

      this.forceOpenCart(cart, trigger);
    }

    showAddToCartSuccess() {
      this.showToast("Added to cart!");

      this.root.querySelectorAll("[data-ks-byob-submit-label]").forEach((el) => {
        if (!el.dataset.prevLabel) el.dataset.prevLabel = el.textContent;
        el.textContent = "Added!";
      });

      const bubble = document.getElementById("cart-icon-bubble");
      bubble?.classList.add("ks-byob-cart-pulse");

      setTimeout(() => {
        bubble?.classList.remove("ks-byob-cart-pulse");
        this.root.querySelectorAll("[data-ks-byob-submit-label]").forEach((el) => {
          if (el.dataset.prevLabel) {
            el.textContent = el.dataset.prevLabel;
            delete el.dataset.prevLabel;
          }
        });
        this.updateUI();
      }, 2200);
    }

    applyManualHashOverrides() {
      this.root.querySelectorAll("[data-ks-byob-pack][data-sb-hash]").forEach((el) => {
        const hash = el.dataset.sbHash;
        const variantId = el.dataset.variantId;
        if (hash && variantId) this.hashMap[variantId] = hash;
      });
    }

    /* ---------------- Simple Bundles auto-discovery ---------------- */

    isAutoDiscoveryMode() {
      return this.packSourceMode === "simple_bundles_auto";
    }

    setDiscoveryState(state) {
      const hasPacks = this.hasPackRows();
      const visibleState = state === "error" && hasPacks ? "ready" : state;
      this.root.querySelectorAll("[data-ks-byob-pack-loading]").forEach((el) => {
        const show = visibleState === "loading";
        el.hidden = !show;
        el.setAttribute("aria-hidden", show ? "false" : "true");
        el.inert = !show;
      });
      this.root.querySelectorAll("[data-ks-byob-pack-error]").forEach((el) => {
        const show = visibleState === "error";
        el.hidden = !show;
        el.setAttribute("aria-hidden", show ? "false" : "true");
        el.inert = !show;
      });
    }

    applyDiscovery(discovery) {
      if (!discovery) return;

      this.hashMap = { ...this.hashMap, ...discovery.hashMap, ...this.getManualOverrides() };
      this.discoveredBoxSizes = discovery.boxSizes || {};
      this.discoveryCurrency = discovery.currencyCode || this.discoveryCurrency;

      if (this.isAutoDiscoveryMode() && discovery.packs && discovery.packs.length) {
        this.renderDiscoveredPacks(discovery.packs);
      }
    }

    /**
     * Snapshot of Liquid-rendered rows, keyed by variant ID. Used to enrich
     * discovered rows (description for the detail modal, merchant-authored
     * titles) without requiring pack products to be publicly listed.
     */
    buildEnrichmentIndex() {
      const index = {};
      this.root.querySelectorAll("[data-ks-byob-pack]").forEach((packEl) => {
        const variantId = packEl.dataset.variantId;
        if (!variantId || packEl.dataset.ksByobDiscovered === "true") return;

        const source = packEl.querySelector(".ks-byob__pack-detail-source");
        index[variantId] = {
          title: packEl.querySelector(".ks-byob__pack-title")?.textContent?.trim() || "",
          subtitle: packEl.querySelector(".ks-byob__pack-subtitle")?.textContent?.trim() || "",
          description:
            source?.querySelector(".ks-byob__pack-detail-description-source")?.innerHTML?.trim() ||
            "",
          detailImage:
            source
              ?.querySelector("[data-ks-byob-pack-detail-image]")
              ?.getAttribute("data-image-url") || "",
          productUrl: packEl.dataset.productUrl || "",
          packImages: packEl.dataset.packImages || "",
          min: packEl.dataset.min,
          max: packEl.dataset.max,
        };
      });
      return index;
    }

    formatPackPrice(cents) {
      if (cents == null) return "";
      return this.formatDisplayMoney(
        cents,
        this.root.querySelector("[data-ks-byob-price]")
      );
    }

    buildDiscoveredPackRow(pack, enrichment) {
      const extra = enrichment[pack.variantId] || {};
      const title = extra.title || pack.title || FALLBACK_PACK_TITLE;
      const subtitle = extra.subtitle || pack.optionValues || "";
      const imageUrl = pack.imageUrl || extra.detailImage || "";
      const price = this.formatPackPrice(pack.priceCents);
      const productUrl = pack.productUrl || extra.productUrl || "";
      let packImages = [];
      try {
        if (extra.packImages) packImages = JSON.parse(extra.packImages) || [];
      } catch (_) {
        packImages = [];
      }
      if (imageUrl && !packImages.some((img) => (img.src || img) === imageUrl)) {
        packImages = [{ src: imageUrl, thumb: imageUrl, alt: title }, ...packImages];
      }

      const li = document.createElement("li");
      li.className = "ks-byob__pack-row";

      const article = document.createElement("article");
      article.className = "ks-byob__pack";
      article.setAttribute("data-ks-byob-pack", "");
      article.dataset.ksByobDiscovered = "true";
      article.dataset.variantId = pack.variantId;
      article.dataset.min = extra.min || "0";
      article.dataset.max = extra.max || "99";
      article.dataset.defaultQty = "0";
      article.dataset.sbHash = pack.hash;
      if (productUrl) article.dataset.productUrl = productUrl;
      if (packImages.length) {
        article.dataset.packImages = JSON.stringify(packImages);
      }
      if (pack.priceCents != null) {
        article.dataset.priceCents = String(pack.priceCents);
      }
      if (!pack.available) article.dataset.unavailable = "true";

      const main = document.createElement("button");
      main.type = "button";
      main.className = "ks-byob__pack-main";
      main.setAttribute("data-ks-byob-pack-detail-trigger", "");
      main.setAttribute("aria-label", "View details for " + title);

      if (imageUrl) {
        const media = document.createElement("div");
        const second = packImages[1];
        media.className =
          "ks-byob__pack-media" + (second ? " ks-byob__pack-media--has-secondary" : "");
        const img = document.createElement("img");
        img.className = "ks-byob__pack-image";
        img.loading = "lazy";
        img.src = imageUrl;
        img.alt = title;
        if (pack.imageWidth) img.width = pack.imageWidth;
        if (pack.imageHeight) img.height = pack.imageHeight;
        media.appendChild(img);
        if (second) {
          const img2 = document.createElement("img");
          img2.className = "ks-byob__pack-image ks-byob__pack-image--secondary";
          img2.loading = "lazy";
          img2.src = second.src || second;
          img2.alt = title;
          media.appendChild(img2);
        }
        main.appendChild(media);
      }

      const info = document.createElement("div");
      info.className = "ks-byob__pack-info";

      const titleEl = document.createElement("h4");
      titleEl.className = "ks-byob__pack-title";
      titleEl.textContent = title;
      info.appendChild(titleEl);

      if (subtitle) {
        const subtitleEl = document.createElement("p");
        subtitleEl.className = "ks-byob__pack-subtitle";
        subtitleEl.textContent = subtitle;
        info.appendChild(subtitleEl);
      }

      if (price) {
        const priceEl = document.createElement("p");
        priceEl.className = "ks-byob__pack-price";
        priceEl.textContent = price;
        info.appendChild(priceEl);
      }

      const hint = document.createElement("span");
      hint.className = "ks-byob__pack-detail-hint";
      hint.textContent = "View details";
      info.appendChild(hint);

      main.appendChild(info);
      article.appendChild(main);

      const qty = document.createElement("div");
      qty.className = "ks-byob__pack-qty";
      qty.setAttribute("data-ks-byob-pack-qty", "");
      qty.innerHTML =
        '<button type="button" class="ks-byob__qty-btn ks-byob__pack-minus" data-ks-byob-pack-minus disabled>' +
        '<svg class="ks-byob__qty-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
        '<path fill="currentColor" fill-rule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd" /></svg>' +
        "</button>" +
        '<span class="ks-byob__pack-count" data-ks-byob-pack-count aria-live="polite">0</span>' +
        '<button type="button" class="ks-byob__qty-btn ks-byob__pack-plus" data-ks-byob-pack-plus>' +
        '<svg class="ks-byob__qty-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
        '<path fill="currentColor" fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" /></svg>' +
        "</button>";
      qty.querySelector("[data-ks-byob-pack-minus]").setAttribute("aria-label", "Remove " + title);
      qty.querySelector("[data-ks-byob-pack-plus]").setAttribute("aria-label", "Add " + title);
      article.appendChild(qty);

      const detailSource = document.createElement("div");
      detailSource.className = "ks-byob__pack-detail-source";
      detailSource.hidden = true;

      const detailImageUrl = extra.detailImage || pack.imageUrl;
      if (detailImageUrl) {
        const imgSource = document.createElement("div");
        imgSource.className = "ks-byob__pack-detail-image-source";
        imgSource.setAttribute("data-ks-byob-pack-detail-image", "");
        imgSource.setAttribute("data-image-url", detailImageUrl);
        imgSource.setAttribute("data-image-alt", title);
        detailSource.appendChild(imgSource);
      }

      const titleSource = document.createElement("div");
      titleSource.className = "ks-byob__pack-detail-title-source";
      titleSource.textContent = title;
      detailSource.appendChild(titleSource);

      if (subtitle) {
        const subtitleSource = document.createElement("div");
        subtitleSource.className = "ks-byob__pack-detail-subtitle-source";
        subtitleSource.textContent = subtitle;
        detailSource.appendChild(subtitleSource);
      }

      if (extra.description) {
        const descSource = document.createElement("div");
        descSource.className = "ks-byob__pack-detail-description-source";
        descSource.innerHTML = extra.description;
        detailSource.appendChild(descSource);
      }

      article.appendChild(detailSource);
      li.appendChild(article);
      return li;
    }

    renderDiscoveredPacks(packs) {
      const lists = this.root.querySelectorAll(
        "[data-ks-byob-pack-list-desktop] .ks-byob__pack-list-inner, [data-ks-byob-pack-list-drawer] .ks-byob__pack-list-inner"
      );
      if (!lists.length) return;

      const enrichment = this.buildEnrichmentIndex();
      const usable = packs.filter((pack) => pack.available);
      const renderable = usable.length ? usable : packs;
      if (!renderable.length) return;

      // Skip the DOM swap when revalidation returned the same pack set, so a
      // background refresh never interrupts an in-progress selection.
      const signature = renderable.map((pack) => pack.variantId + ":" + pack.hash).join("|");
      if (this.discoveryRendered && this._discoverySignature === signature) {
        this.setDiscoveryState("ready");
        this.scheduleHoverImagePrefetch();
        return;
      }
      this._discoverySignature = signature;

      lists.forEach((list) => {
        const fragment = document.createDocumentFragment();
        renderable.forEach((pack) => {
          fragment.appendChild(this.buildDiscoveredPackRow(pack, enrichment));
        });
        list.replaceChildren(fragment);
      });

      this.discoveryRendered = true;
      this.setDiscoveryState("ready");

      // Drop selections for packs Simple Bundles no longer offers.
      const validIds = new Set(renderable.map((pack) => pack.variantId));
      [...this.selections.keys()].forEach((variantId) => {
        if (!validIds.has(variantId)) this.selections.delete(variantId);
      });
      this.selectionOrder = this.selectionOrder.filter((id) => validIds.has(id));

      this.syncPackRowsFromState();
      this.updateUI();
      this.scheduleHoverImagePrefetch();
    }

    async bootstrapHashMap() {
      const cached = loadSessionDiscovery(this.productHandle);

      if (cached) {
        // Paint instantly from cache, then revalidate below so packs added or
        // removed in Simple Bundles show up on the next page load.
        this.applyDiscovery(cached);
      } else if (this.isAutoDiscoveryMode() && !this.hasPackRows()) {
        this.setDiscoveryState("loading");
      }

      await this.ensureHashMapForSelection(true);

      if (this.isAutoDiscoveryMode() && !this.hasPackRows()) {
        this.setDiscoveryState("error");
      } else {
        this.setDiscoveryState("ready");
      }

      this.updateMode();
      this.updateUI();
      this.scheduleHoverImagePrefetch();
    }

    getSelectedVariantIds() {
      const ids = [];
      this.selectionOrder.forEach((variantId) => {
        const qty = this.selections.get(variantId) || 0;
        for (let i = 0; i < qty; i++) ids.push(variantId);
      });
      return [...new Set(this.selections.keys())].filter(
        (id) => (this.selections.get(id) || 0) > 0
      );
    }

    missingHashVariantIds() {
      return this.getSelectedVariantIds().filter((id) => !this.hashMap[id]);
    }

    async ensureHashMapForSelection(forceRefetch) {
      const missing = this.missingHashVariantIds();
      const needsPackRows = this.isAutoDiscoveryMode() && !this.hasPackRows();

      if (!forceRefetch && missing.length === 0 && !needsPackRows) return true;

      if (this._discoveryRequest) {
        await this._discoveryRequest;
        return this.hasPackRows() && this.missingHashVariantIds().length === 0;
      }

      this._discoveryRequest = fetchDiscovery(this.productUrl)
        .then((discovery) => {
          const hasPacks = discovery && Array.isArray(discovery.packs) && discovery.packs.length > 0;
          if (hasPacks || (discovery && Object.keys(discovery.hashMap || {}).length)) {
            this.applyDiscovery(discovery);
          }
          if (discovery && Object.keys(discovery.hashMap || {}).length) {
            saveSessionDiscovery(this.productHandle, discovery);
          }
          return hasPacks || this.hasPackRows();
        })
        .catch(() => {
          const cached = loadSessionDiscovery(this.productHandle);
          if (cached) this.applyDiscovery(cached);
          this.hashMap = { ...this.hashMap, ...this.getManualOverrides() };
          return this.hasPackRows();
        })
        .finally(() => {
          this._discoveryRequest = null;
        });

      const ok = await this._discoveryRequest;
      if (!ok) return false;

      return this.missingHashVariantIds().length === 0;
    }

    getManualOverrides() {
      const overrides = {};
      this.root.querySelectorAll("[data-ks-byob-pack][data-sb-hash]").forEach((el) => {
        if (el.dataset.sbHash && el.dataset.variantId) {
          overrides[el.dataset.variantId] = el.dataset.sbHash;
        }
      });
      return overrides;
    }

    updateMode() {
      const missing = this.missingHashVariantIds();
      const hasSelection = this.getSelectedVariantIds().length > 0;
      this.mode = hasSelection && missing.length > 0 ? "fallback" : "direct";

      this.root.querySelectorAll("[data-ks-byob-actions-direct]").forEach((el) => {
        if (this.mode === "fallback") el.setAttribute("hidden", "");
        else el.removeAttribute("hidden");
      });

      this.root.querySelectorAll("[data-ks-byob-actions-fallback]").forEach((el) => {
        if (this.mode === "fallback") el.removeAttribute("hidden");
        else el.setAttribute("hidden", "");
      });
    }

    setPackQty(variantId, qty, min, max) {
      const required = this.getRequiredCount();
      const prev = this.selections.get(variantId) || 0;
      const totalWithoutThis = this.getTotalSelected() - prev;
      const boxRoom = Math.max(0, required - totalWithoutThis);
      const effectiveMax = Math.min(max, boxRoom);

      qty = Math.max(min, Math.min(effectiveMax, qty));

      if (qty <= 0) {
        this.selections.delete(variantId);
        this.selectionOrder = this.selectionOrder.filter((id) => id !== variantId);
      } else {
        this.selections.set(variantId, qty);
        if (!this.selectionOrder.includes(variantId)) {
          this.selectionOrder.push(variantId);
        }
      }

      if (prev !== qty && qty > 0) {
        this.ensureHashMapForSelection(false).then(() => this.updateMode());
      }
    }

    canAddMorePacks() {
      return this.getTotalSelected() < this.getRequiredCount();
    }

    updateSelectionStatus(total, required) {
      const exact = total === required;
      const over = total > required;
      const justBecameFull =
        exact && this.lastTotalSelected !== required && required > 0;

      this.root.querySelectorAll("[data-ks-byob-progress]").forEach((track) => {
        const ratio =
          required > 0 ? Math.min(1, Math.max(0, total / required)) : 0;
        const pct = Math.round(ratio * 100);
        const fill = track.querySelector("[data-ks-byob-progress-fill]");

        track.style.setProperty("--ks-byob-progress", String(ratio));
        track.classList.toggle("is-full", exact);
        track.classList.toggle("is-over", over);
        track.setAttribute("aria-valuenow", String(total));
        track.setAttribute("aria-valuemin", "0");
        track.setAttribute("aria-valuemax", String(required));
        track.setAttribute("aria-hidden", total === 0 ? "true" : "false");

        if (fill) {
          // Keep width as a fallback for older CSS; scaleX is the primary fill.
          fill.style.width = "100%";
          fill.style.transform = "scaleX(" + ratio + ")";
          fill.setAttribute("data-progress", String(pct));
        }
      });

      this.root.querySelectorAll("[data-ks-byob-selection-status]").forEach((statusEl) => {
        statusEl.classList.remove(
          "ks-byob__selection-status--progress",
          "ks-byob__selection-status--full",
          "ks-byob__selection-status--over"
        );

        // Shipping-final-step UX: no "Choose N packs for this box" helper copy.
        // Only surface a compact over-limit warning when needed.
        if (over) {
          const excess = total - required;
          statusEl.hidden = false;
          statusEl.textContent = `Remove ${excess} pack${excess === 1 ? "" : "s"}`;
          statusEl.classList.add("ks-byob__selection-status--over");
        } else {
          statusEl.hidden = true;
          statusEl.textContent = "";
        }
      });

      if (justBecameFull) {
        this.celebrateBoxFull();
        this.drawerDoneBtn?.classList.add("ks-byob__drawer-done--ready");
      } else if (!exact) {
        this.drawerDoneBtn?.classList.remove("ks-byob__drawer-done--ready");
      }

      this.lastTotalSelected = total;
    }

    syncPackRowsFromState() {
      const atBoxLimit = !this.canAddMorePacks();

      this.root.querySelectorAll("[data-ks-byob-pack]").forEach((packEl) => {
        const variantId = packEl.dataset.variantId;
        const qty = this.selections.get(variantId) || 0;
        const min = parseInt(packEl.dataset.min || "0", 10);
        const max = parseInt(packEl.dataset.max || "99", 10);
        const countEl = packEl.querySelector("[data-ks-byob-pack-count]");
        const minus = packEl.querySelector("[data-ks-byob-pack-minus]");
        const plus = packEl.querySelector("[data-ks-byob-pack-plus]");

        if (countEl) countEl.textContent = String(qty);
        if (minus) minus.disabled = qty <= min;
        if (plus) plus.disabled = atBoxLimit || qty >= max;
        packEl.classList.toggle("is-selected", qty > 0);
        packEl.classList.toggle("is-box-full", atBoxLimit && qty > 0);
      });
    }

    getTotalSelected() {
      let total = 0;
      this.selections.forEach((qty) => {
        total += qty;
      });
      return total;
    }

    getPackPriceCents(variantId) {
      const packEl = this.root.querySelector(
        `[data-ks-byob-pack][data-variant-id="${variantId}"]`
      );
      const cents = parseInt(packEl?.dataset?.priceCents ?? "", 10);
      return Number.isFinite(cents) ? cents : 0;
    }

    /**
     * Display-only total for the packs currently selected. Simple Bundles owns
     * the real cart/checkout price, so this never touches the add-to-cart payload.
     */
    getSelectedTotalCents() {
      let cents = 0;
      this.selections.forEach((qty, variantId) => {
        cents += this.getPackPriceCents(variantId) * qty;
      });
      return cents;
    }

    formatDisplayMoney(cents, priceEl) {
      const currency = priceEl?.dataset?.currency || this.discoveryCurrency;
      if (currency && typeof Intl !== "undefined") {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || "en", {
            style: "currency",
            currency,
          }).format(cents / 100);
        } catch (_) {
          /* fall through to sample-based formatting */
        }
      }

      // `data-money-sample` holds the shop's rendering of zero, so it stays a
      // stable reference even after we overwrite the element's own text.
      const sample = priceEl?.dataset?.moneySample || "";
      const symbol = sample.replace(/[\d.,\s]/g, "").trim() || "$";
      return symbol + formatMoney(cents, { textContent: sample || "0.00" });
    }

    updateSelectedTotalDisplay() {
      const cents = this.getSelectedTotalCents();
      this.root.querySelectorAll("[data-ks-byob-price]").forEach((priceEl) => {
        const parent = priceEl.parentElement;
        if (parent) {
          parent.querySelectorAll(".ks-sale, .ks-promo-badge").forEach((el) => {
            if (el === priceEl) return;
            if (el.classList.contains("ks-sale") || el.classList.contains("ks-promo-badge")) el.remove();
          });
        }
        let node = priceEl.nextElementSibling;
        while (node && (node.classList.contains("ks-sale") || node.classList.contains("ks-promo-badge"))) {
          const remove = node;
          node = node.nextElementSibling;
          remove.remove();
        }
        priceEl.classList.remove("ks-strike");
        priceEl.removeAttribute("data-ks-processed-for");
        priceEl.setAttribute("data-ks-base-cents", String(cents));
        priceEl.textContent = this.formatDisplayMoney(cents, priceEl);
      });
      if (window.KSPromoPreview && typeof window.KSPromoPreview.decorateByob === "function") {
        window.KSPromoPreview.decorateByob(this.root);
      }
    }

    getRequiredCount() {
      if (!this.activePill) return 4;

      const variantTitle = this.activePill.dataset.variantTitle;

      // Manual overrides win, then Simple Bundles item-count, then title digits.
      if (this.countMap && this.countMap[variantTitle]) {
        return this.countMap[variantTitle];
      }

      const discovered = this.discoveredBoxSizes[this.activePill.dataset.variantId];
      if (discovered && discovered.itemCount > 0) return discovered.itemCount;

      return parseRequiredCount(variantTitle, this.countMap);
    }

    getParentVariantId() {
      return this.activePill ? parseInt(this.activePill.dataset.variantId, 10) : 0;
    }

    buildBundleSelectionString() {
      const parts = [];
      this.selectionOrder.forEach((variantId) => {
        const qty = this.selections.get(variantId) || 0;
        const hash = this.hashMap[variantId];
        if (!hash) return;
        for (let i = 0; i < qty; i++) parts.push(hash);
      });
      return parts.join(" <> ");
    }

    getSubmitLabel(total, required) {
      if (total === 0) return `Choose ${required} packs`;
      if (total < required) {
        const remaining = required - total;
        return `Choose ${remaining} more pack${remaining === 1 ? "" : "s"}`;
      }
      if (total > required) {
        const excess = total - required;
        return `Remove ${excess} pack${excess === 1 ? "" : "s"}`;
      }
      return "Add to Cart";
    }

    updateUI() {
      const total = this.getTotalSelected();
      const required = this.getRequiredCount();
      const exact = total === required;
      const over = total > required;

      this.root.querySelectorAll("[data-ks-byob-count-current]").forEach((el) => {
        el.textContent = String(total);
      });
      this.root.querySelectorAll("[data-ks-byob-count-required]").forEach((el) => {
        el.textContent = String(required);
      });

      this.root.querySelectorAll(".ks-byob__count").forEach((el) => {
        el.classList.remove("ks-byob__count--valid", "ks-byob__count--over", "ks-byob__count--full");
        if (exact) {
          el.classList.add("ks-byob__count--valid", "ks-byob__count--full");
        } else if (over) {
          el.classList.add("ks-byob__count--over");
        }
      });

      this.updateSelectionStatus(total, required);

      const label = this.getSubmitLabel(total, required);
      const shippingReady = this.isShippingSelected();
      const canSubmit =
        exact &&
        shippingReady &&
        this.mode === "direct" &&
        !this.loading &&
        this.hasPackRows();

      this.updateCheckoutDockVisibility(exact);

      this.root.querySelectorAll("[data-ks-byob-submit]").forEach((btn) => {
        btn.disabled = !canSubmit;
        const labelEl =
          btn.querySelector("[data-ks-byob-submit-label]") || btn.querySelector(".text");
        if (labelEl) {
          if (!this.hasPackRows()) {
            labelEl.textContent = this.isAutoDiscoveryMode()
              ? "Loading box options…"
              : "Add packs in section settings";
          } else if (exact && !shippingReady) {
            labelEl.textContent = "Choose shipping option";
          } else {
            labelEl.textContent = label;
          }
        }
      });

      if (this.drawerDoneBtn) {
        const canDone = exact && !this.loading && this.hasPackRows();
        this.drawerDoneBtn.disabled = !canDone;
        const doneLabelEl = this.drawerDoneBtn.querySelector(
          "[data-ks-byob-drawer-done-label]"
        );
        if (doneLabelEl) {
          doneLabelEl.textContent = this.getDrawerDoneLabel(total, required);
        }
      }

      this.highlightShippingNextStep(exact && !shippingReady && !this.drawerOpen);
      this.highlightAddToCartNextStep(
        exact && shippingReady && !this.drawerOpen && !this.loading
      );

      this.updateSelectedTotalDisplay();
      if (this.packModalOpen) this.syncModalQtyControls();

      this.updateMode();
    }

    getFormForSubmit() {
      return this.checkoutForm || this.root.querySelector("[data-ks-byob-form]");
    }

    promoElementForActiveBox() {
      const source = this.root;
      const pill = this.activePill;
      const el = source.cloneNode(false);
      if (!pill) return el;
      el.setAttribute("data-ks-variant-id", pill.dataset.variantId || "");
      el.setAttribute("data-ks-variant-title", pill.dataset.variantTitle || "");
      el.setAttribute("data-ks-base-cents", pill.dataset.variantPrice || "");
      return el;
    }

    async handleAddToCart(event) {
      event.preventDefault();
      if (this.loading) return;

      const total = this.getTotalSelected();
      const required = this.getRequiredCount();
      if (total !== required || this.mode !== "direct") return;

      const form = this.getFormForSubmit();
      if (window.KSBoxSizeAtcWorkflow && !event.ksPurchaseConfirmed) {
        const self = this;
        window.KSBoxSizeAtcWorkflow.open({
          trigger: event.currentTarget,
          form: form,
          productTitle: this.activePill?.dataset.variantTitle || "Build Your Own Box",
          variantId: String(this.getParentVariantId() || ""),
          startStep: "shipping",
          source: "byob",
          promoElement: this.promoElementForActiveBox(),
          onConfirm: function (result) {
            event.ksPurchaseConfirmed = true;
            self._confirmedShipping = result;
            return self.handleAddToCart(event);
          },
        });
        return;
      }
      if (
        !event.ksPurchaseConfirmed &&
        window.KSShippingOption &&
        !window.KSShippingOption.validate(form)
      ) {
        this.updateUI();
        return;
      }

      const hashReady = await this.ensureHashMapForSelection(true);
      if (!hashReady || this.missingHashVariantIds().length > 0) {
        this.updateMode();
        return;
      }

      const bundleSelection = this.buildBundleSelectionString();
      if (!bundleSelection) {
        this.updateMode();
        return;
      }

      let shippingValue = "";
      let estimatedShipDate = "";

      if (this._confirmedShipping && this._confirmedShipping.shippingValue) {
        shippingValue = this._confirmedShipping.shippingValue;
        estimatedShipDate = this._confirmedShipping.estimatedShipDate || "";
        this._confirmedShipping = null;
      } else if (window.KSShippingOption) {
        shippingValue = window.KSShippingOption.normalizeShippingOption(
          String(window.KSShippingOption.getSelectedOption(form) || "")
        );
        estimatedShipDate =
          window.KSShippingOption.getEstimatedShipDateForOption(shippingValue);
      }

      const cart = await this.preloadCartSidebar();
      if (!cart || typeof cart.getSectionsToRender !== "function") return;

      if (typeof cart.setActiveElement === "function") {
        cart.setActiveElement(event.currentTarget);
      }

      this.setLoading(true);

      try {
        if (window.KSShippingOption && shippingValue) {
          if (typeof window.KSShippingOption.syncCartShippingSelection === "function") {
            await window.KSShippingOption.syncCartShippingSelection(shippingValue);
          } else {
            await window.KSShippingOption.syncCartAttribute(shippingValue);
          }
        }

        const { fetchConfig } = await import(
          window._resolveImport?.("helper") ||
            window._importmap?.imports?.["helper"] ||
            "helper"
        );

        const cartAddUrl =
          window.routes?.cart_add_url ||
          (window.Shopify?.routes?.root || "/") + "cart/add";

        const formData = new FormData();
        formData.append("id", String(this.getParentVariantId()));
        formData.append("quantity", "1");
        formData.append("properties[_bundle_selection]", bundleSelection);
        if (shippingValue) {
          formData.append("properties[Shipping Option]", shippingValue);
        }
        if (estimatedShipDate) {
          formData.append("properties[Estimated Ship Date]", estimatedShipDate);
        }

        if (typeof cart.getSectionsToRender === "function") {
          formData.append(
            "sections",
            cart.getSectionsToRender().map((s) => s.id).join(",")
          );
          formData.append("sections_url", window.location.pathname);
        }

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];
        config.body = formData;

        const response = await fetch(cartAddUrl, config);
        const data = await response.json();

        if (data.status) {
          throw data;
        }

        if (
          window.KSShippingOption &&
          shippingValue &&
          typeof window.KSShippingOption.syncCartShippingSelection === "function"
        ) {
          await window.KSShippingOption.syncCartShippingSelection(shippingValue);
        }

        this.hideDrawerImmediately();
        await this.openCartAfterAdd(cart, data, event.currentTarget);

        if (typeof cart.reload === "function") {
          try {
            await cart.reload();
          } catch (_) {
            /* drawer already opened from the add response */
          }
        }

        this.forceOpenCart(cart, event.currentTarget);
        this.showAddToCartSuccess();
      } catch (err) {
        console.error("[KS BYOB]", err);
        alert(
          err?.description || err?.message || "Unable to add your box to the cart."
        );
      } finally {
        window.KSShippingOption?.atcTimingPrint?.();
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.root.querySelectorAll("[data-ks-byob-submit]").forEach((btn) => {
        btn.classList.toggle("is-loading", isLoading);
      });
      this.updateUI();
    }

    async openDrawer(trigger) {
      const drawer = this.root.querySelector("[data-ks-byob-drawer]");
      if (!drawer) return;

      this.scrollYBeforeDrawer = window.scrollY;
      this.previousFocus = trigger || document.activeElement;
      drawer.hidden = false;
      requestAnimationFrame(() => drawer.classList.add("is-open"));
      document.body.classList.add("ks-byob-drawer-open");
      document.body.style.overflow = "hidden";
      this.drawerOpen = true;
      this.syncDrawerBoxLabel();
      this.updateUI();

      const closeBtn = drawer.querySelector("[data-ks-byob-close-drawer].ks-byob__drawer-close");
      setTimeout(async () => {
        try {
          const helper = await import(
            window._resolveImport?.("helper") ||
              window._importmap?.imports?.helper ||
              "helper"
          );
          helper.trapFocus?.(drawer.querySelector(".ks-byob__drawer-panel"), closeBtn);
        } catch (_) {
          closeBtn?.focus();
        }
      }, 100);
    }

    closeDrawer(options = {}) {
      const drawer = this.root.querySelector("[data-ks-byob-drawer]");
      if (!drawer) return;

      drawer.classList.remove("is-open");
      this.drawerOpen = false;
      this.clearBodyScrollLock();

      if (options.immediate) {
        drawer.hidden = true;
      } else {
        setTimeout(() => {
          drawer.hidden = true;
        }, 280);
      }

      import(window._resolveImport?.("helper") || "helper")
        .then((helper) => {
          helper.removeTrapFocus?.(null);
        })
        .catch(() => {});

      if (!options.skipScroll) {
        setTimeout(() => {
          this.scrollToMobileCheckout({
            focusShipping: Boolean(options.focusShipping),
            instant: false,
          });
          this.updateUI();
        }, 300);
      } else {
        this.updateUI();
      }
    }
  }

  function initAll() {
    document.querySelectorAll("[data-ks-byob]").forEach((root) => {
      if (root.__ksByobInstance) return;
      root.__ksByobInstance = new ByobBuilder(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", initAll);

  window.KSByobBuilder = {
    ByobBuilder,
    fetchHashMap,
    fetchDiscovery,
    parseDiscoveryFromHtml,
  };
})();
